import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from '@app/@shared/services/dialog/dialog.service';
import { CustomOption, DropDownOption, OptionGroup, SocialOption } from '../generic-toolbar/option-model';
import { StatelessBehavior, ToggleBehavior } from '../generic-toolbar/state-behavior-model';
import { Icon } from '../generic-toolbar/icons';
import { ShareItemModalComponent } from '../share-item-modal/share-item-modal.component';
import { DeepLinkingService } from '@app/@shared/services/deep-linking.service';
import { combineLatest } from 'rxjs';
import { RouteHelper } from '@app/@shared/utils/route-helper';
import { environment } from '@env/environment';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MetaService } from '@shared/services/meta.service';
import { TelemetryEvent } from '@shared/models/telemetry-event';
import { TelemetryEventName } from '@shared/enums';
import { moduleNameFromUrl } from '@shared/utils/common';
import { Analysis, ReportMimeType, QuicksighDashboard } from '@shared/models/reverse-engineering/analysis.models';
import { QuicksightDashboardEmbed } from '@app/@shared/services/market-analysis/dashboard.models';
import { ContentService } from '@app/@shared/services/content/content.service';
import { DashboardContentOptions, DashboardExperience, ExperienceFrameMetadata } from 'amazon-quicksight-embedding-sdk/dist/index';
import { NotificationSettingsService } from '@app/@shared/services/notification-settings/notification-settings.service';
import { FeatureFlagService, ffKeys } from '@app/@shared/services/featureflag.service';

@UntilDestroy()
@Component({
  selector: 'app-quicksight-viewer',
  templateUrl: './quicksight-viewer.component.html',
  styleUrls: ['./quicksight-viewer.component.scss'],
  standalone: false
})
export class QuicksightViewerComponent implements OnInit, AfterViewInit {
  @Input() set assetId(id: string) {
    if (id) {
      this.quicksightAssetId = id;
    }
  }

  @Input() set quicksightAssetEmbed(embed: QuicksightDashboardEmbed) {
    if (embed) {
      this.qsEmbed = embed;
      this.downloadEnabled = embed.downloadEnabled;
      this.hasRawData = embed.hasRawData;
    }
  }

  @Input() analysisMetadata: Analysis;

  @Input() set analysisCode(id: string) {
    if (id && !this.analysisId) {
      this.analysisId = id;
    }
  }

  @Output() closeAssetViewer: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('AssetViewerContainer') assetViewerContainer: ElementRef;
  @ViewChild('ShareDropdownContainer') shareDropdownContainer: ElementRef;

  public toolbarOptions: OptionGroup[] = [];
  public isLoading = false;
  public dashboardInitialState: DashboardContentOptions = {};
  public downloadEnabled = false;
  public hasRawData = false;

  public dashboardUrl: string;
  public qsEmbed: QuicksightDashboardEmbed;

  public isFollowing = signal(false);
  public isFullscreen = signal(false);
  public enableNotificationsFF = toSignal(this.ffService.getFlag(ffKeys.enablePlatformNotifications));

  private quicksightAssetId = '';
  private analysisId = '';
  private channelId: string;
  private subscriptionId: string;
  private reportName: string;
  private reportCode: string;
  private dashboardExperience: DashboardExperience;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly deepLinkingService: DeepLinkingService,
    private readonly dialogService: DialogService,
    private readonly contentService: ContentService,
    private readonly metaService: MetaService,
    private readonly notificationService: NotificationSettingsService,
    private readonly ffService: FeatureFlagService
  ) {
    effect(() => {
      if (this.isFullscreen()) {
        this.toolbarOptions = this.createFullScreenToolbarOption();
      } else {
        this.toolbarOptions = this.createToolbarOptions();
      }
    });
  }

  @HostListener('document:fullscreenchange') exitFullScreenHandler() {
    this.isFullscreen.set(document.fullscreenElement !== null);
  }

  ngOnInit(): void {
    combineLatest([
      this.route.fragment,
      this.route.paramMap
    ]).subscribe(([fragment, routeParams]) => {
      const params = new URLSearchParams(fragment ?? '');
      this.reportName = params.get('reportName');
      this.channelId = routeParams.get('channelId');
      this.subscriptionId = routeParams.get('subscriptionId');
      this.reportCode = routeParams.get('analysisCode');
      this.getQSParameters(params.get('view'));
    });

    this.loadNotificationStatus();
  }

  ngAfterViewInit(): void {
    this.toolbarOptions = this.createToolbarOptions();
  }

  public setDashboardLoaded(event: DashboardExperience) {
    this.dashboardExperience = event;
  }

  public share(stateId?: string): void {
    this.isLoading = false;
    const shareFormModalInstance = this.dialogService.open(ShareItemModalComponent, { windowClass: 'shareModalClass' });
    shareFormModalInstance.componentInstance.isDocumentShareable = true;
    shareFormModalInstance.componentInstance.shareEntityType = 'ASSET';
    shareFormModalInstance.componentInstance.itemDeepLinkUrl = environment.basePath + this.createURL(stateId);
  }

  public shareWithParameters(): void {
    this.isLoading = true;
    Promise.all([
      this.dashboardExperience?.getSelectedSheetId(),
      this.dashboardExperience?.getParameters()
    ]).then(([id, params]) => {
      this.storeDashboardState(id, params);
    }).catch(([_error]) => {
      this.share();
    });
  }

  public closeViewer(): void {
    this.closeAssetViewer.emit();
  }

  public onClickToggleFullscreen(): void {
    if (document.fullscreenElement !== null) {
      document.exitFullscreen();
    } else {
      this.isFullscreen.set(true);
      this.assetViewerContainer.nativeElement.requestFullscreen();
    }
  }

  public viewRawData(): void {
    this.contentService.getQuicksightRawDataEmbeddedURL(this.quicksightAssetId).subscribe(
      (rawDataUrl) => {
        this.dashboardInitialState = {};
        this.qsEmbed = {
          ...this.qsEmbed,
          embedUrl: rawDataUrl
        };
        this.dashboardUrl = rawDataUrl;
      }
    );
  }

  public toggleNotifications(): void {
    if (this.isFollowing()) {
      this.unfollow();
    } else {
      this.follow();
    }
  }

  public follow() {
    const previousState = this.isFollowing();

    // Optimistically update the UI
    this.isFollowing.set(true);

    this.notificationService.followReportProgress(this.analysisMetadata).subscribe({
      next: () => {
        // Already set to true optimistically, no need to set again
      },
      error: () => {
        // Revert to actual previous state on error
        this.isFollowing.set(previousState);
      }
    });
  }

  public unfollow() {
    const previousState = this.isFollowing();

    // Optimistically update the UI
    this.isFollowing.set(false);

    this.notificationService.deleteReportProgressSubscription(this.analysisMetadata.id).subscribe({
      next: () => {
        // Already set to false optimistically, no need to set again
      },
      error: () => {
        // Revert to actual previous state on error
        this.isFollowing.set(previousState);
      }
    });
  }



  public sendTabViewTelemetryEvent(tabName: string): void {
    if (this.analysisMetadata?.entitled) {
      const applicationModule = moduleNameFromUrl(this.router.url);
      const eventPayload = {
        artifactType: 'Tab name',
        artifactName: tabName,
        assetId: this.quicksightAssetId,
        reportCode: this.reportCode,
        reportId: this.analysisMetadata?.id,
        channelId: this.channelId,
        subscriptionId: this.subscriptionId,
        reportTitle: this.analysisMetadata?.name,
      };
      const telemetryEvent = new TelemetryEvent(
        TelemetryEventName.EVENT_QS_TAB_VIEW,
        eventPayload,
        applicationModule
      );
      this.metaService.postTelemetryEvent(telemetryEvent).subscribe();
    }
  }

  private loadNotificationStatus(): void {
    this.notificationService.getReportProgressState(this.analysisMetadata)
        .subscribe({
          next: (result) => {
            if (result && result.notifyFirstWipDeliverable && result.notifyAnalysisPublished) {
              this.isFollowing.set(true);
            } else {
              this.isFollowing.set(false);
            }
          },
          error: (error) => {
            // Failed to check notification status
          }
        });
  }

  private createToolbarOptions(): OptionGroup[] {
    const optionGroups: OptionGroup[] = [];
    const downloadOptions: any[] = [];
    const otherOptions: any[] = [];
    const zoomOptions: any[] = [];
    const closeOption: any[] = [];

    if (this.hasRawData && this.downloadEnabled) {
      downloadOptions.push(
        new CustomOption(
          'download',
          { click: this.viewRawData.bind(this) },
          new StatelessBehavior(Icon.REPORT_VIEWER_DOWNLOAD, 'Download')
        )
      );
      optionGroups.push(new OptionGroup(downloadOptions));
    }

    if (this.enableNotificationsFF()) {
      const notificationTooltip = 'Follow to receive content status updates';
      otherOptions.push(
        new SocialOption(
          'notifications',
          { click: this.toggleNotifications.bind(this) },
          new ToggleBehavior(
            Icon.IMAGE_NOTIFICATION,
            notificationTooltip,
            Icon.IMAGE_NOTIFICATION,
            notificationTooltip,
            this.isFollowing()
          )
        )
      );
    }

    otherOptions.push(
      new DropDownOption('share', Icon.IMAGE_SHARE, 'Share', this.shareDropdownContainer.nativeElement)
    );

    zoomOptions.push(
      new CustomOption(
        'toggle-fullscreen',
        { click: this.onClickToggleFullscreen.bind(this) },
        new StatelessBehavior(Icon.IMAGE_ENTER_FULLSCREEN, 'Full Screen')
      )
    );

    closeOption.push(
      new CustomOption(
        'close-viewer',
        { click: this.closeViewer.bind(this) },
        new StatelessBehavior(Icon.REPORT_VIEWER_CLOSE, 'Close'),
        'Close'
      )
    );

    optionGroups.push(new OptionGroup(zoomOptions));
    optionGroups.push(new OptionGroup(otherOptions));
    optionGroups.push(new OptionGroup(closeOption));
    return optionGroups;
  }

  private createFullScreenToolbarOption(): OptionGroup[] {
    return [
      new OptionGroup([
        new CustomOption(
          'toggle-fullscreen',
          { click: this.onClickToggleFullscreen.bind(this) },
          new StatelessBehavior(Icon.IMAGE_EXIT_FULLSCREEN, 'Exit Full Screen')
        )
      ])
    ];
  }

  private sendTelemetryEvent(): void {
    if (this.analysisMetadata?.entitled) {
      const applicationModule = moduleNameFromUrl(this.router.url);
      const eventPayload = {
        artifactType: ReportMimeType.QUICKSIGHT,
        artifactName: `${this.reportName}.qs`,
        assetId: this.quicksightAssetId,
        reportCode: this.reportCode,
        reportId: this.analysisMetadata?.id,
        channelId: this.channelId,
        subscriptionId: this.subscriptionId,
        reportTitle: this.analysisMetadata?.name,
      };
      const telemetryEvent = new TelemetryEvent(
        TelemetryEventName.EVENT_QS_ASSET_VIEW,
        eventPayload,
        applicationModule
      );
      this.metaService.postTelemetryEvent(telemetryEvent).subscribe();
    }
  }

  private storeDashboardState(id: string, params: any): void {
    const qsState = {
      sheetId: id,
      parameters: params
    };
    this.contentService.postQuicksightSheetAndParameters(qsState).subscribe(
      (stateId) => this.share(stateId),
      () => this.share()
    );
  }

  private createURL(stateId: string): string {
    const fragment = new URLSearchParams();
    fragment.set('assetId', this.quicksightAssetId);
    if (stateId) fragment.set('view', stateId);

    return this.router.serializeUrl(
      this.router.createUrlTree(['analysis-view', this.analysisId], {
        fragment: fragment.toString(),
        relativeTo: RouteHelper.getModuleRouteOrShell(this.route),
      })
    );
  }

  private getQSParameters(viewId): void {
    if (viewId) {
      this.contentService.getQuicksightSheetAndParameters(viewId).subscribe(
        (state: QuicksighDashboard) => {
          this.dashboardInitialState = {
            parameters: state.parameters,
            sheetOptions: {
              initialSheetId: state.sheetId
            },
          };
          this.dashboardUrl = this.qsEmbed?.embedUrl;
          this.sendTelemetryEvent();
        },
        (err: any) => {
          switch (err.status) {
            case 403:
              this.deepLinkingService.redirectTo403();
              break;
            case 404:
              this.router.navigate(['/404']);
              break;
          }
        });
    } else {
      this.dashboardInitialState = {};
      this.dashboardUrl = this.qsEmbed?.embedUrl;
      this.sendTelemetryEvent();
    }
  }
}
